import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Wizard state hook with debounced autosave.
 * - Loads project by draftToken
 * - Provides project, update(patch), saving flag, lastSaved, saveNow
 * - Auto saves every 5s if dirty AND on field-blur via save()
 */
export function useWizardState(draftToken) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const dirtyRef = useRef(false);
  const pendingRef = useRef({});

  // Load
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/projects/draft/${draftToken}`)
      .then((r) => {
        if (alive) setProject(r.data);
      })
      .catch(() => {
        if (alive) setProject(null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [draftToken]);

  const flush = useCallback(async () => {
    if (Object.keys(pendingRef.current).length === 0) return;
    const patch = pendingRef.current;
    pendingRef.current = {};
    setSaving(true);
    try {
      await api.patch(`/projects/draft/${draftToken}`, patch);
      // IMPORTANT: do NOT setProject(response) — that overwrites text the user
      // typed while the PATCH was in flight. Local state is the source of truth
      // during editing; server response only confirms persistence.
      setLastSaved(new Date());
      dirtyRef.current = Object.keys(pendingRef.current).length > 0;
    } catch (e) {
      // re-merge on failure (preserving any newer pending changes)
      pendingRef.current = { ...patch, ...pendingRef.current };
    } finally {
      setSaving(false);
    }
  }, [draftToken]);

  // 5s interval autosave
  useEffect(() => {
    const t = setInterval(() => {
      if (dirtyRef.current) flush();
    }, 5000);
    return () => clearInterval(t);
  }, [flush]);

  // Save before unload
  useEffect(() => {
    const handler = () => {
      if (Object.keys(pendingRef.current).length > 0) {
        navigator.sendBeacon &&
          navigator.sendBeacon(
            `${api.defaults.baseURL}/projects/draft/${draftToken}`,
            new Blob([JSON.stringify(pendingRef.current)], { type: "application/json" })
          );
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [draftToken]);

  const update = useCallback((patch) => {
    setProject((p) => ({ ...(p || {}), ...patch }));
    pendingRef.current = { ...pendingRef.current, ...patch };
    dirtyRef.current = true;
  }, []);

  const saveNow = flush;

  return { project, loading, saving, lastSaved, update, saveNow, setProject };
}
